import { ARTICLES_KEY, API_BASE } from '../utils/constants';
import { adminFetch } from './apiClient';

const SYNC_PENDING_KEY = 'portal_sync_pending';
const LAST_FULL_SYNC_KEY = 'portal_last_full_sync';
const FULL_SYNC_INTERVAL = 12 * 60 * 60 * 1000; // 12 hours

const getArticles = () => {
  try {
    const data = localStorage.getItem(ARTICLES_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const isEphemeralUrl = (u) =>
  typeof u === 'string' && (u.startsWith('data:') || u.startsWith('blob:'));

const stripEphemeralMedia = (article) => {
  // Strip base64 data URLs (hundreds of KB) and blob: URLs (session-only, break on any
  // other device) from article objects before writing to localStorage or syncing to the
  // backend.  Both URL types are useless outside the current browser session:
  //   data: → bloats localStorage quota and the MySQL articles table
  //   blob: → URL.createObjectURL() is tab-local; dead to all other visitors
  let stripped = article;
  if (isEphemeralUrl(stripped.featuredImage)) {
    stripped = { ...stripped, featuredImage: '' };
  }
  if (isEphemeralUrl(stripped.videoUrl)) {
    stripped = { ...stripped, videoUrl: '' };
  }
  if (Array.isArray(stripped.galleryImages)) {
    const cleanGallery = stripped.galleryImages
      .map(u => (isEphemeralUrl(u) ? '' : (u || '')))
      .filter(Boolean);
    if (cleanGallery.length !== stripped.galleryImages.length ||
        cleanGallery.some((u, i) => u !== stripped.galleryImages[i])) {
      stripped = { ...stripped, galleryImages: cleanGallery };
    }
  }
  return stripped;
};
// Keep legacy alias so existing callers inside this file stay consistent
const stripBase64 = stripEphemeralMedia;

const saveArticles = (articles) => {
  // Always strip base64 images before writing to localStorage to prevent the 5 MB
  // quota from being exhausted. Quota errors previously wiped ALL articles' images.
  const forStorage = articles.map(stripBase64);
  try {
    localStorage.setItem(ARTICLES_KEY, JSON.stringify(forStorage));
  } catch (e) {
    if (e && (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
      // Still over quota (too many articles) — keep only the newest 50
      try { localStorage.setItem(ARTICLES_KEY, JSON.stringify(forStorage.slice(0, 50))); } catch {}
    } else {
      throw e;
    }
  }
  // Notify any NewsContext instances in the same tab that articles changed
  try { window.dispatchEvent(new CustomEvent('adminArticlesUpdated')); } catch {}
};

// ── Pending sync queue — tracks articles that failed to sync to backend ────────

const getPendingSyncs = () => {
  try {
    const raw = localStorage.getItem(SYNC_PENDING_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const markSyncPending = (id, operation, data) => {
  try {
    const pending = getPendingSyncs();
    pending[id] = { operation, data, ts: Date.now() };
    localStorage.setItem(SYNC_PENDING_KEY, JSON.stringify(pending));
  } catch {}
};

const markSyncDone = (id) => {
  try {
    const pending = getPendingSyncs();
    if (pending[id]) {
      delete pending[id];
      localStorage.setItem(SYNC_PENDING_KEY, JSON.stringify(pending));
    }
  } catch {}
};

// Fire-and-forget backend sync with up to 3 attempts (3s delay between retries).
// On permanent failure, queues the operation in pending-sync storage for next login.
const trySyncToBackend = (operation, id, data) => {
  // Never send blob:/data: URLs to the backend — they are session-only and would
  // be stored as broken URLs in the DB for all other visitors.
  const payload = data ? stripEphemeralMedia(data) : data;
  const run = async (attemptsLeft) => {
    try {
      let res;
      if (operation === 'delete') {
        res = await adminFetch(`/api/admin/articles/${id}`, { method: 'DELETE' });
      } else if (operation === 'update') {
        res = await adminFetch(`/api/admin/articles/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        res = await adminFetch('/api/admin/articles', { method: 'POST', body: JSON.stringify(payload) });
      }
      if (res && res.ok) {
        markSyncDone(id);
        return;
      }
    } catch {}
    if (attemptsLeft > 1) {
      await new Promise(r => setTimeout(r, 3000));
      return run(attemptsLeft - 1);
    }
    // All attempts failed — queue for next admin login
    markSyncPending(id, operation, payload);
  };
  run(3).catch(() => {});
};

// Retry any previously queued (failed) syncs — called on admin login
export const syncPendingToBackend = async () => {
  const pending = getPendingSyncs();
  const entries = Object.entries(pending);
  if (entries.length === 0) return;
  await Promise.allSettled(
    entries.map(async ([id, { operation, data }]) => {
      try {
        let res;
        if (operation === 'delete') {
          res = await adminFetch(`/api/admin/articles/${id}`, { method: 'DELETE' });
        } else {
          res = await adminFetch('/api/admin/articles', { method: 'POST', body: JSON.stringify(data) });
        }
        if (res && res.ok) markSyncDone(id);
      } catch {}
    })
  );
};

// Push ALL localStorage articles to backend (migration for articles published before this fix).
// Throttled to once per 12 hours per browser session — only if at least one article synced.
export const syncAllToBackend = async () => {
  const lastSync = localStorage.getItem(LAST_FULL_SYNC_KEY);
  if (lastSync && Date.now() - parseInt(lastSync, 10) < FULL_SYNC_INTERVAL) return;
  const articles = getArticles();
  if (articles.length === 0) return;
  const results = await Promise.allSettled(
    articles.map(article =>
      adminFetch('/api/admin/articles', {
        method: 'POST',
        body: JSON.stringify(article),
      })
    )
  );
  const anySucceeded = results.some(
    r => r.status === 'fulfilled' && r.value && r.value.ok
  );
  if (anySucceeded) {
    localStorage.setItem(LAST_FULL_SYNC_KEY, Date.now().toString());
  }
};

export const getAllAdminArticles = () => {
  return getArticles();
};

export const getAdminArticle = (id) => {
  const articles = getArticles();
  return articles.find(a => a.id === id) || null;
};

export const addArticle = (articleData) => {
  const articles = getArticles();
  const newArticle = {
    ...articleData,
    id: 'admin_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    views: 0,
    isAdmin: true,
    isRss: false,
  };
  articles.unshift(newArticle);
  saveArticles(articles);
  trySyncToBackend('create', newArticle.id, newArticle);
  return newArticle;
};

export const updateArticle = (id, data) => {
  const articles = getArticles();
  const index = articles.findIndex(a => a.id === id);
  if (index === -1) return null;
  articles[index] = { ...articles[index], ...data, updatedAt: new Date().toISOString() };
  saveArticles(articles);
  trySyncToBackend('update', id, articles[index]);
  return articles[index];
};

export const deleteArticle = (id) => {
  const articles = getArticles();
  const filtered = articles.filter(a => a.id !== id);
  saveArticles(filtered);
  trySyncToBackend('delete', id, null);
  return true;
};

export const toggleArticleVisibility = (id) => {
  const articles = getArticles();
  const article = articles.find(a => a.id === id);
  if (!article) return null;
  article.status = article.status === 'published' ? 'draft' : 'published';
  article.updatedAt = new Date().toISOString();
  saveArticles(articles);
  trySyncToBackend('update', id, article);
  return article;
};

const DEVANAGARI_RE = /[ऀ-ॿ]/;

/** Normalize admin articles so they have the same fields the frontend expects */
const normalize = (article) => {
  // Language resolution order:
  // 1. Devanagari in titleHindi → always Hindi (strongest signal)
  // 2. Admin's explicit lang field → respect the form toggle selection
  // 3. No lang set + only English title → English (legacy fallback)
  // 4. Default → Hindi (this is a Hindi-first portal)
  let lang;
  if (DEVANAGARI_RE.test(article.titleHindi || '')) {
    // Devanagari script in the Hindi title — definitely Hindi
    lang = 'hi';
  } else if (article.lang) {
    // Admin explicitly chose a language in the form — respect it
    lang = article.lang;
  } else if (article.titleEnglish && !(article.titleHindi || '').trim()) {
    // No explicit lang set and only an English title — English article (legacy detection)
    lang = 'en';
  } else {
    lang = 'hi';
  }

  return {
    ...article,
    title: article.titleHindi || article.titleEnglish || article.title || '',
    pubDate: article.pubDate || article.createdAt,
    image: article.featuredImage || article.image || '',
    excerpt: article.summary || article.excerpt || '',
    content: article.body || article.content || '',
    source: article.source || 'Kaushal Prime Nation',
    lang,
  };
};

export const getPublishedAdminArticles = () => {
  return getArticles().filter(a => a && a.status === 'published').map(normalize);
};

// Fetches published articles from backend to populate NewsContext.
// Returns empty array on any error — never throws.
export const fetchPublishedArticlesFromBackend = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/articles/published`, {
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return [];
    if (!(res.headers.get('content-type') || '').includes('application/json')) return [];
    const data = await res.json();
    return (data.articles || []).filter(a => a.status === 'published').map(normalize);
  } catch {
    return [];
  }
};

export const getFeaturedArticles = () => {
  return getArticles().filter(a => a && a.featured && a.status === 'published').map(normalize);
};

export const getBreakingArticles = () => {
  return getArticles().filter(a => a && a.breaking && a.status === 'published').map(normalize);
};
